import moment from 'moment'
import store from '@/store'
import { allowWarnings, nullString } from '@/utils'
import { playSound } from './sounds'
import { getTaskDefaultActivity } from './tasks'
import Vue from 'vue'
import api from '@/api'
import { singularize } from '@/utils/utils'
import { getTimeRemaining } from '@/utils/store'

export function updateTimeEntry (entry, date, start, end, task, description, user) {
  let newDate = moment(date)
  let startMoment = moment(start)
  let newStart = startMoment.date(newDate.date()).month(newDate.month()).year(newDate.year())

  let patch = {
    id: entry.id,
    attributes: {
      start: dateToDbUtcString(newStart),
      end: dateToDbUtcString(end),
      description: description || entry.attributes.description
    },

    relationships: {
      user: {
        data: {
          type: 'users',
          id: user ? user.id : entry.relationships.user.data.id
        }
      }
    }
  }
  patch.relationships[singularize(task.type)].data = {type: task.type, id: task.id}
  store.dispatch('time-entries/update', patch).then(_ => {
    store.dispatch('time-entries/loadById', {id: entry.id})
  })
}

// ---------------- Date ---------------- //

export function setTimeEntryDate (entry, date) {
  let oldStart = moment(entry.attributes.start)
  let newDate = moment(date)
  let start = oldStart.date(newDate.date()).month(newDate.month()).year(newDate.year())

  let patch = {
    id: entry.id,
    attributes: {
      start: dateToDbUtcString(start)
    }
  }
  store.dispatch('time-entries/update', patch).then(_ => { store.dispatch('time-entries/loadById', {id: entry.id}) })
}

// ---------------- Time ---------------- //

export function setTimeEntryStartTime (entry, time) {
  let oldStart = moment(entry.start)
  let newStart = moment(time)
  let newTime = oldStart.hour(newStart.hour()).minute(newStart.minute())
  let patch = {
    id: entry.id,
    attributes: {
      start: dateToDbUtcString(newTime)
    }
  }
  store.dispatch('time-entries/update', patch).then(_ => { store.dispatch('time-entries/loadById', {id: entry.id}) })
}

export function setTimeEntryStopTime (entry, time) {
  let patch = {
    id: entry.id,
    attributes: {
      end: dateToDbUtcString(time)
    }
  }
  store.dispatch('time-entries/update', patch).then(_ => { store.dispatch('time-entries/loadById', {id: entry.id}) })
}

// ---------------- Tasks ---------------- //

export function setTimeEntryTask (entry, task) {
  let timeEntry = store.getters['time-entries/byId']({ id: entry.id })
  let activity = getTaskDefaultActivity(task)
  timeEntry.relationships[singularize(task.type)].data = { type: task.type, id: task.id }
  timeEntry.relationships.activity.data = activity.id ? { type: 'activities', id: activity.id } : null
  store.dispatch('time-entries/update', timeEntry).then(_ => { store.dispatch('time-entries/loadById', {id: entry.id}) })
}

export function getTimeEntryTask (entry) {
  let ret = null
  try {
    ret = store.getters['tasks/byId']({ id: entry.relationships.task.data.id })
  } catch (e) { if (allowWarnings) console.warn(e) }
  return ret || {attributes: {name: nullString}}
}

// ---------------- Clients ---------------- //

export function getTimeEntryClient (entry) {
  let ret = null
  try {
    let client = getTimeEntryTask(entry).relationships.client
    ret = store.getters['clients/byId']({ id: client.data.id })
  } catch (e) { if (allowWarnings) console.warn(e) }

  return ret || {attributes: {name: nullString}}
}

// ---------------- Locked ---------------- //

export function setTimeEntryLocked (entry, status) {
  if (Array.isArray(entry)) {
    let data = []
    entry.forEach(ent => {
      ent.attributes.isLocked = status
      data.push({
        'type': 'time-entries',
        'id': ent.id,
        'attributes': {'is-locked': status} })
    })
    return new Promise((resolve, reject) => {
      api.post('/time-entries', {
        data
      }).then(() => { resolve() })
    })
  } else {
    entry.attributes.isLocked = status
    return new Promise((resolve, reject) => {
      store.dispatch('time-entries/update', entry).then(_ => {
        resolve()
      })
    })
  }
}

// ---------------- Billed ---------------- //

export function setTimeEntryBilled (entry, status) {
  entry.attributes.isBilled = status
  store.dispatch('time-entries/update', entry).then(_ => { store.dispatch('time-entries/loadById', {id: entry.id}) })
}

// ---------------- Submitted ---------------- //

export function setTimeEntrySubmitted (entry, status) {
  if (Array.isArray(entry)) {
    let data = []
    entry.forEach(ent => {
      ent.attributes.isSubmitted = status
      data.push({
        'type': 'time-entries',
        'id': ent.id,
        'attributes': {'is-submitted': status} })
    })
    return new Promise((resolve, reject) => {
      api.post('/time-entries', {
        data
      }).then(() => { resolve() })
    })
  } else {
    entry.attributes.isSubmitted = status
    return new Promise((resolve, reject) => {
      store.dispatch('time-entries/update', entry).then(_ => {
        resolve()
      })
    })
  }
}

// ---------------- Description ---------------- //

export function setTimeEntryDescription (entry, description) {
  entry.attributes.description = description
  store.dispatch('time-entries/update', entry).then(_ => { store.dispatch('time-entries/loadById', {id: entry.id}) })
}

export function setTimeEntryActivity (entry, activity) {
  entry.relationships.activity = { data: { id: activity.id, type: 'activities' } }
  store.dispatch('time-entries/update', entry).then(() => { store.dispatch('time-entries/loadById', { id: entry.id }) })
}

export function setTimeEntryNotes (note, newText) {
  note.attributes.note = newText
  store.dispatch('time-entry-notes/update', note)
}

export function createTimeEntryNotes (parent, text, user) {
  let currentUser = store.state.authentication.user
  let currentUserId = currentUser.id || '1'
  let userId = user ? user.id : currentUserId
  let noteData = {
    attributes: {
      note: text,
      adminMode: false
    },
    relationships: {
      timeEntry: {
        data: {
          type: 'time-entries',
          id: parent.id
        }
      },
      user: {
        data: {
          type: 'user',
          id: userId
        }
      }
    }
  }
  store.dispatch('time-entry-notes/create', noteData).then(note => {
    parent.relationships.notes.data.push({type: 'time-entry-notes', id: note.id})
    store.dispatch('time-entries/update', parent)
  })
}

export function startNewTimeEntry (task, startTime, noteBody, stopTime, user) {
  store.commit('updatePausedEntry', null)
  if (!stopTime) stopCurrentEntry()
  let currentUser = store.state.authentication.user
  let currentUserId = currentUser.id || '1'
  let userId = user ? user.id : currentUserId
  let description = noteBody || ''
  let timeEntryData = {
    attributes: {
      start: dateToDbUtcString(startTime),
      isLocked: false,
      isBilled: false,
      description: description
    },
    relationships: {
      user: {
        data: {
          type: 'users',
          id: userId
        }
      }
    }
  }
  if (task) {
    let activity = getTaskDefaultActivity(task)
    timeEntryData.relationships[singularize(task.type)] = {
      data:
      {
        type: task.type,
        id: task.id
      }
    }
    timeEntryData.relationships.activity = {
      data: {
        type: 'activities',
        id: activity.id
      }
    }
  }
  if (stopTime) {
    timeEntryData.attributes.end = dateToDbUtcString(stopTime)
  }

  store.dispatch('time-entries/create', timeEntryData).then(test => {
    playSound('positive')
    if (task) {
      getTimeRemaining(task)
    }
  })
}

export function stopCurrentEntry () {
  let user = store.state.authentication.user
  let entries = store.getters['time-entries/all']

  // Filter by user
  entries = entries.filter(entry => {
    return entry.relationships.user.data.id === user.id
  })
  entries.forEach(entry => {
    if (entry.attributes.end === undefined) {
      setTimeEntryStopTime(entry, 'now')
    }
  })
}

// ---------------- Formatters ---------------- //

export function formatTimeString (time) {
  return time ? moment(time).format('HH:mm') : ''
}

export function formatDateString (date, sortable) {
  let ret = moment(date).format('ddd MM-DD')
  if (sortable) {
    ret = moment(date).format('YYYY-MM-DD HH:mm')
  }
  return ret
}

export function dateToDbUtcString (now) {
  var m = moment(now)
  let ret = m.format('YYYY-MM-DD HH:mm:ss ZZ')
  if (now === 'now') {
    ret = 'now'
  }
  return ret
}

export function normalizeStartEnd (start, end) {
  let startTime = moment(start)
  let endTime = moment(end)
  let duration = moment.duration(endTime.diff(startTime))

  if (duration.asHours() > 24) {
    while (duration.asHours() > 24) {
      endTime.subtract(1, 'day')
      duration = moment.duration(endTime.diff(startTime))
    }
  } else if (duration.asMinutes() < 0 && duration.asMinutes() > -1) { // This is a wierd state
    endTime.add(1, 'minute')
    duration = moment.duration(endTime.diff(startTime))
  } else {
    while (duration.asHours() < 0) {
      endTime.add(1, 'day')
      duration = moment.duration(endTime.diff(startTime))
    }
  }

  return {startTime, endTime}
}

export function mapTimeEntries (entries) {
  entries.forEach(entry => {
    let task = store.getters['tasks/byId']({ id: entry.relationships.task.data && entry.relationships.task.data.id })
    let event = store.getters['events/byId']({ id: entry.relationships.event.data && entry.relationships.event.data.id })
    let milestone = store.getters['milestones/byId']({ id: entry.relationships.milestone.data && entry.relationships.milestone.data.id })
    entry.task = task || event || milestone

    let project = entry.task ? store.getters['projects/byId']({ id: entry.task.relationships.project.data && entry.task.relationships.project.data.id }) : null
    let client = project ? store.getters['clients/byId']({ id: project.relationships.client.data && project.relationships.client.data.id }) : null
    let user = store.getters['users/byId']({ id: entry.relationships.user.data.id })
    let defaultActivity = entry.task ? store.getters['activities/byId']({ id: entry.task.relationships.defaultActivity.data && entry.task.relationships.defaultActivity.data.id }) : null
    let activity = entry.relationships.activity ? store.getters['activities/byId']({ id: entry.relationships.activity.data && entry.relationships.activity.data.id }) : {id: null}
    let validActivities = project ? project.relationships.validActivities.data.map(data => store.getters['activities/byId']({ id: data.id })) : []
    let calculatedDuration = (Math.round(Number(Number(moment.duration(moment(entry.attributes.end).diff(moment(entry.attributes.start))).asHours())) * 10) / 10).toFixed(1)

    // THIS IS NECESSARY. DO NOT REMOVE THIS STEP NEXT TIME.
    // Sometimes the default activity is not included in the list of valid activities, so add it in and then filter the list to make sure there are no duplicates.
    validActivities.push(defaultActivity)
    validActivities = validActivities.filter(function (item, index) {
      return validActivities.indexOf(item) >= index
    })

    entry.project = project ? project.attributes.name : null
    entry.isPersonal = entry.task && entry.task.attributes.tags && entry.task.attributes.tags.includes('FluxPersonal')
    entry.isHoliday = entry.task && entry.task.attributes.tags && entry.task.attributes.tags.includes('FluxHoliday')
    entry.isPTO = entry.task && entry.task.attributes.tags && entry.task.attributes.tags.includes('FluxPTO')
    entry.projectPath = entry.task && entry.task.attributes.path ? entry.task.attributes.path.replace('|', ' | ') : entry.project
    entry.client = client ? client.attributes.name : null
    entry.notes = entry.relationships.notes.data.map(note => store.getters['time-entry-notes/byId']({ id: note.id }))
    if (entry.attributes.durationInHours) {
      Vue.set(entry, 'duration', entry.attributes.durationInHours)
    } else {
      Vue.set(entry, 'duration', calculatedDuration)
    }

    entry.date = formatDateString(entry.attributes.start, true)
    entry.user = user
    entry.userName = user.attributes.name || null
    entry.defaultActivity = defaultActivity
    entry.activity = activity
    entry.validActivities = validActivities
    entry.locked = entry.attributes.isLocked
    entry.description = entry.attributes.description
    entry.submitted = entry.attributes.isSubmitted
    entry.start = moment(entry.attributes.start)
    entry.end = entry.attributes.end ? moment(entry.attributes.end) : null
    Vue.set(entry, 'selected', false)
    entry.taskString = `${entry.client || '- - -'} | ${entry.projectPath} | ${entry.task ? entry.task.attributes.name : '- - -'}`
    entry.currentActivityName = activity ? activity.attributes.name : '- - -'
    entry.billingRate = entry.attributes.billingRate
    entry.taskName = entry.task ? entry.task.attributes.name : ' - - -'
    entry.tags = entry.task ? entry.task.attributes.tags || '- - -' : '- - -'
  })

  entries.sort((a, b) => {
    let aName = a.user ? a.user.attributes.name.toLowerCase() : ''
    let bName = b.user ? b.user.attributes.name.toLowerCase() : ''

    return aName < bName ? -1 : aName > bName ? 1 : (a.start).isBefore(b.start) ? -1 : (a.start).isAfter(b.start) ? 1 : 0
  })

  entries.forEach((entry, index) => {
    if (!isPTOorHoliday(entry)) {
      entry.overlapsStart = index !== 0 && entry.user === entries[index - 1].user && (entry.start).isBefore(entries[index - 1].end) && !isPTOorHoliday(entries[index - 1])
      entry.overlapsEnd = index < entries.length - 1 && entry.user === entries[index + 1].user && entry.end && (entry.end).isAfter(entries[index + 1].start) && !isPTOorHoliday(entries[index + 1])
    }
  })

  return entries
}

function isPTOorHoliday (entry) {
  if (entry.task && entry.task.attributes.tags) {
    return (entry.task.attributes.tags.includes('FluxPTO') || entry.task.attributes.tags.includes('FluxHoliday'))
  }
  return false
}

export function noMap (entries) {
  return entries
}
